import React, { useEffect, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import Hls from 'hls.js';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { PlayerContainer, Video } from './LivestreamWatchPage.styles';

const ErrorMessage = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  background: rgba(0, 0, 0, 0.7);
  padding: 1rem;
  border-radius: 4px;
  text-align: center;
  z-index: 10;
`;

const LoadingMessage = styled(ErrorMessage)`
  background: rgba(0, 0, 0, 0.5);
`;

const fetchWithRetry = async (url, maxRetries = 10, delay = 1000) => {
  let lastError;
  for (let i = 0; i < maxRetries; i++) {
    try {
      const response = await fetch(url, {
        credentials: 'include'
      });
      
      if (response.ok) {
        return response;
      }
      
      if (response.status === 404) {
        throw new Error('Stream is not available yet. Please try again in a few moments.');
      }
      
      if (response.status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      }
      
      throw new Error(`HTTP error! status: ${response.status}`);
    } catch (error) {
      lastError = error;
      if (i < maxRetries - 1) {
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  throw lastError;
};

/**
 * HlsPlayer component
 *
 * @param {string} hlsUrl - The HLS .m3u8 URL
 * @param {string} status - The livestream status
 * @param {boolean} autoPlay - Whether to autoplay the video
 * @param {function} onError - Callback for error handling
 * @param {node} scoreboard - The scoreboard component to render inside the video container
 */
function HlsPlayer({ 
  hlsUrl, 
  status = 'SCHEDULED',
  autoPlay = false,
  onError,
  scoreboard
}) {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const containerRef = useRef(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const { user } = useSelector((state) => state.auth);
  const urlRef = useRef(hlsUrl);
  const statusRef = useRef(status);

  // Update refs when props change
  useEffect(() => {
    urlRef.current = hlsUrl;
    statusRef.current = status;
  }, [hlsUrl, status]);

  // Component mount logging
  useEffect(() => {
    setIsMounted(true);

    return () => {
      setIsMounted(false);
      // Use inline cleanup instead of the callback to avoid circular dependency
      if (hlsRef.current) {
        try {
          if (videoRef.current) {
            hlsRef.current.detachMedia();
          }
          hlsRef.current.destroy();
          hlsRef.current = null;
        } catch (err) {
        }
      }
      setIsInitialized(false);
    };
  }, []);

  // Check if component is visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, []);

  // Initialize HLS
  const initializeHls = useCallback(async () => {
    if (!isMounted || !isVisible || !videoRef.current || !urlRef.current) {
      return;
    }

    if (isInitialized) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Check if HLS is supported
      if (!Hls.isSupported()) {
        throw new Error('HLS is not supported in your browser');
      }

      // Check if the stream is available
      try {
        const response = await fetchWithRetry(urlRef.current);
        // Stream availability check logging removed for production
      } catch (err) {
        throw err;
      }

      // Clean up any existing instance
      if (hlsRef.current) {
        try {
          if (videoRef.current) {
            hlsRef.current.detachMedia();
          }
          hlsRef.current.destroy();
          hlsRef.current = null;
        } catch (err) {
        }
      }
      setIsInitialized(false);

      // Create new HLS instance
      
      const hls = new Hls({
        xhrSetup: (xhr) => {
          xhr.withCredentials = true;
        },

        backBufferLength: 90,
        maxBufferLength: 30,
        maxMaxBufferLength: 600,
        maxBufferSize: 60 * 1000 * 1000,
        maxBufferHole: 0.5
      });

      hlsRef.current = hls;

      // Add event listeners
      hls.on(Hls.Events.MEDIA_ATTACHED, () => {

        // Try to play immediately after media is attached
        if (autoPlay && videoRef.current) {
          const playPromise = videoRef.current.play();
          if (playPromise !== undefined) {
            playPromise.catch(err => {
              // Don't show error for autoplay failures
              // The user can click play manually
            });
          }
        }
      });

      hls.on(Hls.Events.MANIFEST_PARSED, () => {

        setIsInitialized(true);
        setIsLoading(false);
      });

      hls.on(Hls.Events.ERROR, (event, data) => {
        // HLS error logging removed for production

        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              setError('Network error. Please check your connection.');
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              hls.recoverMediaError();
              break;
            default:
              setError('Failed to load stream. Please try again.');
              // Use inline cleanup instead of the callback to avoid circular dependency
              if (hlsRef.current) {
                try {
                  if (videoRef.current) {
                    hlsRef.current.detachMedia();
                  }
                  hlsRef.current.destroy();
                  hlsRef.current = null;
                } catch (err) {
                }
              }
              setIsInitialized(false);
              break;
          }
        }
      });

      // Load source and attach media
      hls.loadSource(urlRef.current);
      hls.attachMedia(videoRef.current);

    } catch (err) {
      setError(err.message);
      if (onError) {
        onError(err.message);
      }
      // Use inline cleanup instead of the callback to avoid circular dependency
      if (hlsRef.current) {
        try {
          if (videoRef.current) {
            hlsRef.current.detachMedia();
          }
          hlsRef.current.destroy();
          hlsRef.current = null;
        } catch (err) {
        }
      }
      setIsInitialized(false);
    }
  }, [isMounted, isVisible, isInitialized, autoPlay, onError]);

  // Main effect for initialization
  useEffect(() => {
    if (isMounted && isVisible && !isInitialized) {
      initializeHls();
    }
    return () => {
      if (!isVisible) {
        // Use inline cleanup instead of the callback to avoid circular dependency
        if (hlsRef.current) {
          try {
            if (videoRef.current) {
              hlsRef.current.detachMedia();
            }
            hlsRef.current.destroy();
            hlsRef.current = null;
          } catch (err) {
          }
        }
        setIsInitialized(false);
      }
    };
  }, [isMounted, isVisible, isInitialized, initializeHls]);

  // Add video event listeners
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleError = (e) => {
      // Video error logging removed for production
    };

    video.addEventListener('error', handleError);
    return () => video.removeEventListener('error', handleError);
  }, []);

  return (
    <PlayerContainer 
      ref={containerRef}
      style={{ 
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#000',
        position: 'relative',
        width: '100%',
        height: '100%'
      }}
    >
      <Video
        ref={videoRef}
        playsInline
        controls
        style={{ 
          width: '100%', 
          height: '100%',
          objectFit: 'contain',
          background: '#000',
          visibility: isVisible ? 'visible' : 'hidden',
          position: 'relative',
          zIndex: 1
        }}
      />
      {scoreboard && (
        <div style={{
          position: 'absolute',
          top: '1rem',
          left: '1rem',
          zIndex: 9999,
          pointerEvents: 'none'
        }}>
          {scoreboard}
        </div>
      )}
      {isLoading && (
        <LoadingMessage>
          Loading stream...
        </LoadingMessage>
      )}
      {error && !error.includes('autoplay') && (
        <ErrorMessage>
          {typeof error === 'string' ? error : error?.message || 'An error occurred'}
        </ErrorMessage>
      )}
    </PlayerContainer>
  );
}

HlsPlayer.propTypes = {
  hlsUrl: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
  autoPlay: PropTypes.bool,
  onError: PropTypes.func,
  scoreboard: PropTypes.node
};

export default React.memo(HlsPlayer);
